import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, fileSrc } from "@/lib/api";
import { StatusBadge, ConfidenceBadge, kindIcon } from "@/components/StatusBadge";
import { Loader2, Search as SearchIcon, Sparkles } from "lucide-react";

const KINDS = ["", "pdf", "image", "document", "spreadsheet", "presentation", "video", "audio", "text"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [kind, setKind] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async (query, k) => {
    if (!query?.trim()) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/search", { query, kind: k || null });
      setData(data);
    } catch { setData({ results: [], count: 0 }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const query = params.get("q") || "";
    setQ(query);
    if (query) run(query, kind);
  }, [params, kind, run]);

  const submit = (e) => { e.preventDefault(); setParams(q ? { q } : {}); };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-white tracking-tight">Search & Discovery</h1>
        <p className="text-slate-400 mt-2">Ask in natural language. Results are ranked by AI query understanding, with matching excerpts and source links.</p>
      </div>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Show me everything related to the Atlas project"
            className="w-full bg-[#070B14] border border-white/10 rounded-lg pl-12 pr-4 py-3 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none" />
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="search-kind-filter" className="bg-[#070B14] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-blue-500">
          {KINDS.map((k) => <option key={k} value={k}>{k ? k : "All types"}</option>)}
        </select>
        <button type="submit" data-testid="search-submit" className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-6 py-3 transition-colors">Search</button>
      </form>

      {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>}

      {!loading && data && (
        <div className="space-y-5">
          {data.intent && (
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 flex items-start gap-3">
              <Sparkles className="w-4 h-4 text-blue-400 mt-0.5" />
              <div>
                <div className="text-sm text-blue-200">{data.intent}</div>
                {data.expanded_terms?.length > 0 && <div className="text-xs text-slate-500 mt-1">Interpreted as: {data.expanded_terms.join(", ")}</div>}
              </div>
            </div>
          )}
          <div className="text-sm text-slate-500">{data.count} result(s)</div>
          {data.results?.length === 0 && <p className="text-slate-600 py-8 text-center">No matches found.</p>}
          <div className="space-y-3">
            {data.results?.map((r) => (
              <Link key={r.id} to={`/file/${r.id}`} data-testid={`search-result-${r.id}`} className="flex gap-4 rounded-xl border border-white/5 bg-[#111827] p-4 hover:border-blue-500/30 transition-colors">
                <div className="w-24 h-20 rounded-lg overflow-hidden bg-[#070B14] flex items-center justify-center flex-shrink-0 text-blue-400">
                  {r.kind === "image" ? <img src={fileSrc(r.id)} alt="" className="w-full h-full object-cover" /> : kindIcon(r.kind, "w-8 h-8")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold text-white truncate">{r.title}</h3>
                    <StatusBadge status={r.status} />
                    <ConfidenceBadge value={r.confidence} />
                  </div>
                  {r.excerpt && <p className="text-sm text-slate-400 mt-1 line-clamp-2">{r.excerpt}</p>}
                  <div className="text-[11px] text-blue-400/80 mt-1.5">{r.why}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {!loading && !data && <p className="text-slate-600 text-center py-16">Enter a query to begin.</p>}
    </div>
  );
}
